import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { ShieldCheck, ShieldOff, Download, Trash2, X, KeyRound, FileJson, AlertTriangle } from "lucide-react";

const CONSENT_LABELS = {
  marketing: "Communications marketing (courriels promotionnels)",
  ai_processing: "Traitement de mes données par l'assistant IA (suggestions, brouillons)",
};

export default function Settings() {
  const { user, loading: authLoading, setUser } = useAuth();
  const navigate = useNavigate();
  const [totp, setTotp] = useState(null);
  const [consents, setConsents] = useState(null);
  const [setup, setSetup] = useState(null); // {otpauth_uri, qr_code_data_uri}
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        api.get("/me/2fa/status"),
        api.get("/me/privacy/consents"),
      ]);
      setTotp(t.data);
      setConsents(c.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const beginSetup = async () => {
    setError("");
    try {
      const res = await api.post("/me/2fa/setup");
      setSetup(res.data);
      setCode("");
      setRecoveryCodes([]);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const confirmSetup = async () => {
    setError("");
    try {
      const res = await api.post("/me/2fa/confirm", { code });
      setRecoveryCodes(res.data.recovery_codes);
      setSetup(null);
      setCode("");
      setNotice("Double authentification activée.");
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const disable2fa = async () => {
    setError("");
    try {
      await api.post("/me/2fa/disable", { code });
      setDisableOpen(false);
      setCode("");
      setNotice("Double authentification désactivée.");
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const downloadRecovery = () => {
    const blob = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nexa-codes-de-secours.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportData = async () => {
    setError("");
    try {
      const res = await api.get("/me/privacy/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "nexa-mes-donnees.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const toggleConsent = async (kind, granted) => {
    setError("");
    try {
      await api.post("/me/privacy/consents", { kind, granted });
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteAccount = async () => {
    setError("");
    try {
      await api.post("/me/privacy/delete", {
        confirmation: deleteConfirm,
        password: deletePassword || undefined,
      });
      setUser(null);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user) return null;

  return (
    <div data-testid="settings-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-brand-turquoise" />
            Paramètres & confidentialité
          </h1>
          <p className="text-white/70 text-sm mt-1">Double authentification, consentements, export et suppression de vos données (RGPD).</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div data-testid="settings-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}
        {notice && (
          <div data-testid="settings-notice" className="text-sm text-brand-navy bg-brand-turquoise/10 border border-brand-turquoise/30 rounded-lg px-4 py-3">{notice}</div>
        )}

        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-5 h-5 text-brand-turquoise" />
            <h2 className="font-bold text-brand-navy">Double authentification (2FA)</h2>
            {totp && (
              <span data-testid="2fa-status" className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${totp.enabled ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                {totp.enabled ? "Activée" : "Désactivée"}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Un code à 6 chiffres généré par votre application d'authentification (Google Authenticator, 1Password…) sera exigé à chaque connexion.
          </p>

          {totp && !totp.enabled && !setup && (
            <button
              data-testid="2fa-enable-button"
              onClick={beginSetup}
              className="inline-flex items-center gap-2 bg-brand-blue text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors"
            >
              <ShieldCheck className="w-4 h-4" /> Activer la 2FA
            </button>
          )}

          {setup && (
            <div data-testid="2fa-setup-dialog" className="border border-slate-200 rounded-lg p-5 space-y-4">
              <div className="flex flex-col sm:flex-row gap-5 items-center">
                <img data-testid="2fa-qr-code" src={setup.qr_code_data_uri} alt="QR code TOTP" className="w-40 h-40 rounded-lg border border-slate-200" />
                <div className="text-sm text-slate-600 space-y-2">
                  <p>1. Scannez ce QR code avec votre application d'authentification.</p>
                  <p>2. Saisissez le code à 6 chiffres affiché pour confirmer.</p>
                  <p className="text-xs text-slate-400 break-all">Clé manuelle : {setup.otpauth_uri.split("secret=")[1]?.split("&")[0]}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="2fa-confirm-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
                />
                <button
                  data-testid="2fa-confirm-button"
                  onClick={confirmSetup}
                  disabled={code.length !== 6}
                  className="bg-brand-blue text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy disabled:opacity-40"
                >
                  Confirmer
                </button>
              </div>
            </div>
          )}

          {recoveryCodes.length > 0 && (
            <div data-testid="2fa-recovery-codes" className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-5">
              <p className="text-sm font-semibold text-amber-800 mb-2">Codes de secours — affichés une seule fois</p>
              <p className="text-xs text-amber-700 mb-3">Si vous perdez votre téléphone, chaque code permet une connexion unique.</p>
              <div className="grid grid-cols-2 gap-1.5 font-mono text-sm text-brand-navy mb-3">
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <button
                data-testid="2fa-recovery-download"
                onClick={downloadRecovery}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue border border-slate-300 rounded-lg px-3 py-2 hover:bg-white"
              >
                <Download className="w-3.5 h-3.5" /> Télécharger les codes
              </button>
            </div>
          )}

          {totp?.enabled && (
            <div className="flex items-center gap-4">
              <p className="text-xs text-slate-400">
                {totp.recovery_codes_left} code(s) de secours restant(s)
              </p>
              {!disableOpen ? (
                <button
                  data-testid="2fa-disable-button"
                  onClick={() => { setDisableOpen(true); setCode(""); }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50"
                >
                  <ShieldOff className="w-3.5 h-3.5" /> Désactiver
                </button>
              ) : (
                <div className="flex gap-2 items-center">
                  <input
                    data-testid="2fa-disable-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Code actuel"
                    className="w-32 border border-slate-300 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
                  />
                  <button
                    data-testid="2fa-disable-confirm"
                    onClick={disable2fa}
                    disabled={code.length !== 6}
                    className="text-xs font-semibold text-white bg-red-600 rounded-lg px-3 py-2 hover:bg-red-700 disabled:opacity-40"
                  >
                    Confirmer la désactivation
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-bold text-brand-navy mb-2">Consentements</h2>
          <p className="text-sm text-slate-500 mb-4">Chaque modification est consignée dans le journal de consentements (version {consents?.marketing?.version || "2026-06"}).</p>
          {consents && Object.entries(CONSENT_LABELS).map(([kind, label]) => (
            <div key={kind} className="flex items-center justify-between py-3 border-b border-slate-50">
              <span className="text-sm text-brand-navy">{label}</span>
              <button
                data-testid={`consent-toggle-${kind}`}
                onClick={() => toggleConsent(kind, !consents[kind]?.granted)}
                aria-pressed={consents[kind]?.granted}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 shrink-0 ${consents[kind]?.granted ? "bg-brand-turquoise" : "bg-slate-300"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${consents[kind]?.granted ? "translate-x-6" : "left-0.5"}`} />
              </button>
            </div>
          ))}
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <FileJson className="w-5 h-5 text-brand-turquoise" />
            <h2 className="font-bold text-brand-navy">Export de mes données (RGPD)</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Téléchargez l'ensemble de vos données personnelles au format JSON. Les secrets (mots de passe, clés 2FA, jetons) ne sont jamais exportés.
          </p>
          <button
            data-testid="privacy-export-button"
            onClick={exportData}
            className="inline-flex items-center gap-2 bg-brand-blue text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors"
          >
            <Download className="w-4 h-4" /> Télécharger mes données
          </button>
        </section>

        <section className="bg-white rounded-xl border border-red-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="font-bold text-red-600">Zone dangereuse</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            La suppression efface vos données personnelles (CV, fichiers, annonces) et anonymise vos contenus publics. Les enregistrements financiers légalement requis sont conservés sans identifiant. <strong>Action irréversible.</strong>
          </p>
          <button
            data-testid="delete-account-button"
            onClick={() => { setDeleteOpen(true); setDeleteConfirm(""); setDeletePassword(""); }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 border border-red-300 rounded-lg px-5 py-2.5 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Supprimer mon compte
          </button>
        </section>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="delete-account-dialog" className="w-full max-w-md bg-white rounded-2xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-bold text-red-600">Suppression définitive du compte</h2>
              <button data-testid="delete-account-close" onClick={() => setDeleteOpen(false)} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-brand-navy">Votre mot de passe</label>
                <input
                  data-testid="delete-password-input"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-navy">
                  Saisissez exactement : <span className="font-mono text-red-600">SUPPRIMER MON COMPTE</span>
                </label>
                <input
                  data-testid="delete-confirmation-input"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>
              <button
                data-testid="delete-account-confirm"
                onClick={deleteAccount}
                disabled={deleteConfirm !== "SUPPRIMER MON COMPTE"}
                className="w-full bg-red-600 text-white font-semibold rounded-lg px-4 py-3 hover:bg-red-700 transition-colors disabled:opacity-40"
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
